import { FIELDS_USING_ID } from '@api/instant_survey/utilities/constants';

export interface PromptAnswer {
  attributeId?: number | string;
  field: string;
  fieldType: string;
  formattedDisplay?: string;
  id?: number | string;
  operator?: string;
  promptId: number | string;
  values?: PromptAnswerValues;
}

type PromptAnswerValue =
  | number
  | string
  | {
      id?: number | string;
      name?: number | string;
    };

type PromptAnswerValues = PromptAnswerValue[] & {
  date_range?: Array<number | string>;
};

interface TransformedPromptAnswer {
  fe_notes: {
    insights_values: Array<{ name: PromptAnswerValue | undefined }> | PromptAnswerValue[];
  };
  field: string;
  field_type: string;
  id?: number | string;
  operator?: string;
  prompt_id: number | string;
  value: number | string | undefined;
}

/**
 * Transforms modal prompt answers into the snake_case payload expected by survey draft APIs.
 */
export const transformPromptAnswers = (
  promptAnswers: PromptAnswer[]
): TransformedPromptAnswer[] => {
  return promptAnswers.map(({ field, fieldType, id, operator, promptId, values }) => {
    let value: number | string | undefined;
    let insightsValues: TransformedPromptAnswer['fe_notes']['insights_values'];

    if (fieldType === 'COMPARISON_PERIOD') {
      const comparisonPeriod = values?.[0] as number | string | undefined;
      insightsValues = [{ name: comparisonPeriod }];
      value = comparisonPeriod;
    } else if (fieldType === 'DATE') {
      const dateRange = Array.isArray(values?.date_range) ? values.date_range : values;
      value = dateRange?.join(';');
      insightsValues = dateRange || [];
    } else {
      insightsValues = values || [];
      const filterCondition = (mappedValue: null | PromptAnswerValue | undefined) =>
        mappedValue !== undefined && mappedValue !== null;
      value =
        Array.isArray(values) && values.length > 0
          ? values
              .map((answerValue) =>
                typeof answerValue === 'object'
                  ? FIELDS_USING_ID.includes(field)
                    ? answerValue.id
                    : answerValue.name
                  : answerValue
              )
              .filter(filterCondition)
              .join(';')
          : '';
    }
    return {
      fe_notes: { insights_values: insightsValues },
      field,
      field_type: fieldType,
      id,
      operator,
      prompt_id: promptId,
      value,
    };
  });
};

/**
 * Determines whether a purchase-volume answer uses the three-value percentage format
 * (light, medium, heavy) instead of the two-value light/heavy range format.
 */
export const isPercentPurchaseVolumeAnswer = (answer: Partial<PromptAnswer> = {}) => {
  const valuesToCheck = [answer.attributeId, answer.field, answer.formattedDisplay];

  return valuesToCheck.some((valueToCheck) =>
    valueToCheck?.toString().toLowerCase().includes('percent')
  );
};

export const hasValidPercentPurchaseVolume = (values: Array<number | string> = []) => {
  const [light, medium, heavy] = values;
  const nums = [light, medium, heavy].map(Number);

  return (
    nums.every((num) => Number.isFinite(num) && num >= 0) &&
    nums.reduce((sum, num) => sum + num, 0) === 100
  );
};

export const hasValidLightHeavyRange = (values: Array<number | string> = []) => {
  const [light, heavy] = values;
  const lightNum = Number(light);
  const heavyNum = Number(heavy);

  if (!Number.isFinite(lightNum) || !Number.isFinite(heavyNum)) return false;
  if (lightNum < 0 || heavyNum < 0) return false;

  return heavyNum > lightNum;
};
